import { useRef, useState } from 'react'

// Points d'accroche de la hauteur du sheet, en fraction de la hauteur visible.
const SNAP_FRACTIONS = [0.45, 0.7, 0.92] as const
const MIN_SNAP = SNAP_FRACTIONS[0]
const MAX_FRACTION = 0.95
const DEFAULT_FRACTION = 0.7
// Relâché sous cette fraction (et si un onClose est fourni), on glisse hors écran
// et on ferme au lieu de réaccrocher au plus petit palier.
const CLOSE_FRACTION = 0.3

export interface BottomSheetDrag {
  /** Style inline du sheet (hauteur courante) ; undefined avant tout drag / en desktop. */
  sheetStyle: React.CSSProperties | undefined
  /** Hauteur courante en fraction de la hauteur visible ; null avant tout drag / en desktop. */
  fraction: number | null
  grabberProps: React.HTMLAttributes<HTMLDivElement>
}

/**
 * Geste d'un bottom sheet mobile, unifié pour tous les panneaux :
 * - tirer la poignée vers le haut agrandit la hauteur (accrochage au palier le
 *   plus proche au relâchement) ;
 * - vers le bas, on rétrécit jusqu'au plus petit palier, puis le sheet se met à
 *   glisser ; relâché assez bas, il glisse hors écran et `onClose` ferme.
 *
 * La hauteur de repos vit dans le state ; la translation de fermeture est pilotée
 * en impératif sur `sheetRef` (transform composité, fluide, et repart propre au
 * remontage). Le consommateur crée `sheetRef` (via useRef) et le pose lui-même
 * sur l'élément — on ne le renvoie pas, sinon le lire au render déclenche
 * react-hooks/refs. Inactif quand `enabled` est faux (desktop : panneau statique,
 * sheetStyle = undefined rend la main aux classes).
 */
export function useBottomSheetDrag(
  enabled: boolean,
  sheetRef: React.RefObject<HTMLElement | null>,
  onClose?: () => void,
): BottomSheetDrag {
  const [height, setHeight] = useState<number | null>(null)
  const gesture = useRef<{ startH: number; startY: number; raw: number } | null>(null)

  // Reset en phase de render quand on repasse desktop : sheetStyle redevient
  // undefined et React retire la hauteur inline (cf. idiome prevFilterKey ailleurs).
  if (!enabled && height !== null) setHeight(null)

  const grabberProps: React.HTMLAttributes<HTMLDivElement> = {
    onPointerDown: (e) => {
      if (!enabled) return
      // Partir de la hauteur réellement affichée (mesurée si pas encore pilotée)
      // pour que la poignée ne saute pas sous le doigt.
      const startH =
        height ?? sheetRef.current?.getBoundingClientRect().height ?? window.innerHeight * DEFAULT_FRACTION
      gesture.current = { startH, startY: e.clientY, raw: startH }
      setHeight(startH)
      if (sheetRef.current) sheetRef.current.style.transition = 'none'
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    onPointerMove: (e) => {
      const g = gesture.current
      if (!g) return
      const vh = window.innerHeight
      // Tirer vers le haut (clientY décroît) agrandit, vers le bas réduit.
      const raw = Math.min(vh * MAX_FRACTION, g.startH + (g.startY - e.clientY))
      g.raw = raw
      const minH = vh * MIN_SNAP
      if (raw >= minH) {
        // Zone resize : on pilote la hauteur, pas de translation.
        setHeight(raw)
        if (sheetRef.current) sheetRef.current.style.transform = 'translateY(0)'
      } else {
        // Sous le plus petit palier : hauteur figée au plancher, le sheet glisse
        // vers le bas (feel cloche), prêt à se fermer.
        setHeight(minH)
        if (sheetRef.current) sheetRef.current.style.transform = `translateY(${minH - raw}px)`
      }
    },
    onPointerUp: settle,
    onPointerCancel: settle,
  }

  function settle(e: React.PointerEvent<HTMLDivElement>) {
    const g = gesture.current
    if (!g) return
    gesture.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    const vh = window.innerHeight
    const el = sheetRef.current

    if (onClose && g.raw < vh * CLOSE_FRACTION) {
      // Tiré assez bas → glissement hors écran puis fermeture.
      if (!el) return onClose()
      el.style.transition = 'transform 200ms ease-out'
      el.style.transform = 'translateY(100%)'
      const onEnd = (ev: TransitionEvent) => {
        if (ev.propertyName !== 'transform') return
        el.removeEventListener('transitionend', onEnd)
        onClose()
      }
      el.addEventListener('transitionend', onEnd)
      return
    }

    // Sinon : accroche au palier le plus proche et revient en place.
    const snapped = SNAP_FRACTIONS.map((f) => f * vh).reduce((a, b) =>
      Math.abs(b - g.raw) < Math.abs(a - g.raw) ? b : a,
    )
    setHeight(snapped)
    if (el) {
      el.style.transition = 'transform 200ms ease-out'
      el.style.transform = 'translateY(0)'
    }
  }

  return {
    sheetStyle: enabled && height != null ? { height, maxHeight: 'none' } : undefined,
    fraction: enabled && height != null ? height / window.innerHeight : null,
    grabberProps,
  }
}

/**
 * Barre de préhension d'un bottom sheet. `className` porte la visibilité au bon
 * breakpoint (le sheet n'existe qu'en mobile) : `lg:hidden` pour les panneaux
 * de fiche, `md:hidden` pour le tiroir de filtres, `''` pour la cloche (déjà
 * rendue uniquement en mobile).
 */
export function SheetGrabber({
  drag,
  label,
  className = 'lg:hidden',
}: {
  drag: BottomSheetDrag
  label: string
  className?: string
}) {
  return (
    <div
      {...drag.grabberProps}
      role="separator"
      aria-label={label}
      className={`flex shrink-0 cursor-grab touch-none justify-center pt-2 pb-1.5 active:cursor-grabbing ${className}`}
    >
      <span className="h-1 w-10 rounded-full bg-border" />
    </div>
  )
}
